// Decision 84 #1 (owner, 29 Oct 2026: "account for every file"; "make sure the upload-path fix covers this exact case:
// app closed or phone locked part-way through a batch"). Files picked in the Inbox are recorded before any is saved,
// each file is read at once and kept on its own, and every file of a selection is counted from what the server
// confirmed: sent, already on file, still waiting on this phone, refused, or never kept.
import { db, getMeta, setMeta, type FileBlob, type Pick } from "./db";
import { keepBytes, notKeptReason, readBytes, reportNotKept } from "./outbox";
import { newId } from "../lib/ids";

/** This open of the app. A pick another open left without its file was never kept (that page closed part-way). */
const PAGE = newId();
/** Picks this page is still saving. */
const saving = new Set<string>();

/** Decision 84 #1 round 2 (F4 b): at most this many picked files, or this many bytes, are read and not yet kept at once. */
const GROUP_FILES = 20;
const GROUP_BYTES = 100 * 1024 * 1024;

/** The picked files in groups of at most GROUP_FILES files and GROUP_BYTES bytes (File.size), in the order picked. */
function groupsOf(list: File[]): number[][] {
  const groups: number[][] = [];
  let group: number[] = [];
  let bytes = 0;
  list.forEach((f, i) => {
    const size = Number.isFinite(f.size) && f.size > 0 ? f.size : 0;
    if (group.length && (group.length >= GROUP_FILES || bytes + size > GROUP_BYTES)) { groups.push(group); group = []; bytes = 0; }
    group.push(i);
    bytes += size;
  });
  if (group.length) groups.push(group);
  return groups;
}

/** Starts reading one file now; never throws (a file that cannot be read gives its error). */
const startRead = (f: File) => readBytes(f).then((bytes) => ({ bytes }), (error: unknown) => ({ error }));

/** One pick per file, in the order chosen, with a name for a camera photo that has none. */
function makePicks(list: File[], source: "upload" | "camera", selection: string, batch: string | undefined): Pick[] {
  const at = new Date().toISOString();
  return list.map((f, i) => ({ id: newId(), selection, ...(batch ? { upload_batch: batch } : {}), name: f.name || `photo-${Date.now()}-${i + 1}.jpg`,
    size: f.size, type: f.type || "application/octet-stream", source, picked_at: at, order: i, page: PAGE }));
}

/** Writes the list of picks in one transaction, and clears the marks of earlier picks of the same files; a failure is only logged. */
async function recordPicks(picks: Pick[], selection: string, onRecorded?: () => void): Promise<void> {
  try {
    await db.picks.bulkPut(picks);
    // A file named earlier as never kept, picked again now, is accounted for by the new pick.
    const again = new Set(picks.map((p) => `${p.name}|${p.size}`));
    const stale = await db.picks.filter((p) => p.selection !== selection && again.has(`${p.name}|${p.size}`)).toArray();
    const staleFiles = await db.files.bulkGet(stale.map((p) => p.id));
    const gone = stale.filter((p, i) => neverKept(p, staleFiles[i])).map((p) => p.id);
    if (gone.length) await db.picks.bulkDelete(gone);
    onRecorded?.();
  } catch (e) {
    // The list could not be written: the files are still kept one by one below.
    console.warn(`The picked files could not be listed on the phone: ${e instanceof Error ? e.message : String(e)}`);
  }
}

/** Keeps one picked file on the phone once its bytes are read; false (named in the log and on the pick) when it could not be kept. */
async function keepOne(p: Pick, file: File, read: ReturnType<typeof startRead>, source: "upload" | "camera", batch: string | undefined): Promise<boolean> {
  try {
    const bytes = await read;
    if ("error" in bytes) throw bytes.error;
    await keepBytes(Promise.resolve(bytes.bytes), file, p.name, source, { id: p.id, ...(batch ? { upload_batch: batch } : {}) });
    return true;
  } catch (e) {
    reportNotKept(p.name, file, e);
    await db.picks.update(p.id, { not_kept: notKeptReason(p.name) }).catch(() => undefined);
    return false;
  } finally {
    saving.delete(p.id);
  }
}

/**
 * Records what was picked (one transaction, names and sizes only) and keeps each file on its own. The first group's bytes
 * are read at once, before anything is awaited, so a phone locked straight after the pick loses nothing of it; later
 * groups are read once the group before is kept (Decision 84 #1 round 2, F4 b: never more than 20 files or 100 MB read
 * and not yet kept at once). A file of a later group that can no longer be read is named like any other. Returns the
 * picks and the names of the files that could not be kept.
 */
export async function keepPicked(list: File[], source: "upload" | "camera", onRecorded?: () => void): Promise<{ selection: string; picks: Pick[]; notKept: string[] }> {
  const groups = groupsOf(list);
  // Read the first group now, while the picked files can still be read (iOS: not once the page is in the background).
  const reads = new Map<number, ReturnType<typeof startRead>>();
  for (const i of groups[0] ?? []) reads.set(i, startRead(list[i]!));
  const selection = newId();
  // Files chosen together are one set (for example a seller's capture photos of one animal, BD-83).
  const batch = list.length > 1 ? newId() : undefined;
  const picks = makePicks(list, source, selection, batch);
  for (const p of picks) saving.add(p.id);
  const notKept: string[] = [];
  await recordPicks(picks, selection, onRecorded);
  for (const group of groups) {
    for (const i of group) if (!reads.has(i)) reads.set(i, startRead(list[i]!));
    for (const i of group) {
      const p = picks[i]!;
      const read = reads.get(i)!;
      reads.delete(i);
      if (!(await keepOne(p, list[i]!, read, source, batch))) notKept.push(p.name);
    }
  }
  return { selection, picks, notKept };
}

/** A pick with no file kept on the phone: it was refused when saved, or another open of the app never finished saving it. */
function neverKept(p: Pick, file: FileBlob | undefined): boolean {
  // Round 3 (Low): a pick of THIS page that is no longer being saved and has no file was never kept either, even when
  // its not_kept mark could not be written; it is named, never counted as waiting.
  return !file && (!!p.not_kept || !saving.has(p.id));
}

export type SelectionSummary = { selection: string; picked: number; sent: number; duplicates: number; waiting: number; failed: number; not_kept: number };

/** One selection counted from the phone's store: "sent" only for what the server confirmed. */
export async function summarize(selection: string): Promise<SelectionSummary> {
  const picks = await db.picks.where("selection").equals(selection).toArray();
  const files = await db.files.bulkGet(picks.map((p) => p.id));
  const s: SelectionSummary = { selection, picked: picks.length, sent: 0, duplicates: 0, waiting: 0, failed: 0, not_kept: 0 };
  picks.forEach((p, i) => {
    const f = files[i];
    if (f?.uploaded) { if (f.duplicate) s.duplicates++; else s.sent++; }
    else if (f?.failed_reason) s.failed++;
    else if (neverKept(p, f)) s.not_kept++;
    else s.waiting++;
  });
  return s;
}

/** The latest selection picked in the Inbox, if any. */
export async function latestSelection(): Promise<string | null> {
  return (await db.picks.orderBy("picked_at").last())?.selection ?? null;
}

const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;

/** "13 sent, 2 waiting (still on this phone), 1 already on file": nothing is called sent before the server confirms it. */
export function summaryText(s: SelectionSummary): string {
  const parts = [`${s.sent} sent`];
  if (s.waiting) parts.push(`${s.waiting} waiting (still on this phone)`);
  if (s.duplicates) parts.push(`${s.duplicates} already on file`);
  if (s.failed) parts.push(`${s.failed} not accepted (see On this phone)`);
  if (s.not_kept) parts.push(`${s.not_kept} could not be kept on this phone; pick ${s.not_kept === 1 ? "it" : "them"} again`);
  return `Of the ${n(s.picked, "file", "files")} you picked: ${parts.join(", ")}.`;
}

/** The last summary shown for each picked set (Decision 84 #1 round 2, F6 a), kept on the phone. */
const SHOWN_KEY = "selection-summaries-shown";
let shownChain: Promise<unknown> = Promise.resolve();

/**
 * The sets whose summary changed since it was last shown: a set's summary toast is shown once per change of state, not
 * again every round while, say, one file keeps meeting a server error. Serialised, so two rounds never both show it.
 */
export function changedSummaries(selections: SelectionSummary[]): Promise<SelectionSummary[]> {
  const run = shownChain.then(async () => {
    if (!selections.length) return [];
    const shown = await getMeta<Record<string, string>>(SHOWN_KEY, {});
    const fresh = selections.filter((s) => shown[s.selection] !== summaryText(s));
    if (fresh.length) {
      for (const s of fresh) shown[s.selection] = summaryText(s);
      // Only sets still on the phone are remembered.
      const live = new Set((await db.picks.orderBy("selection").uniqueKeys()).map(String));
      for (const k of Object.keys(shown)) if (!live.has(k)) delete shown[k];
      await setMeta(SHOWN_KEY, shown);
    }
    return fresh;
  });
  shownChain = run.catch(() => undefined);
  return run;
}

/** A file on this phone not yet on the server, as the Inbox lists it. */
export type OnPhone = { id: string; name: string; state: "waiting" | "failed"; reason?: string; kept: boolean };

/** Every file of this phone not yet on the server: waiting, refused (the server's reason), or never kept. */
export async function onThisPhone(): Promise<OnPhone[]> {
  const out: OnPhone[] = [];
  const seen = new Set<string>();
  for (const f of await db.files.toArray()) {
    seen.add(f.id);
    if (!f.uploaded) out.push({ id: f.id, name: f.filename, state: f.failed_reason ? "failed" : "waiting", ...(f.failed_reason ? { reason: f.failed_reason } : {}), kept: true });
  }
  for (const p of await db.picks.toArray()) {
    if (!seen.has(p.id) && neverKept(p, undefined)) out.push({ id: p.id, name: p.name, state: "failed", reason: p.not_kept ?? notKeptReason(p.name), kept: false });
  }
  // Failed first (they need the person), then by name.
  return out.sort((a, b) => (a.state === b.state ? a.name.localeCompare(b.name) : a.state === "failed" ? -1 : 1));
}

/** Retry: the file is sent again as a NEW operation, so a refused register is never replayed. */
export async function retryFile(id: string): Promise<void> {
  await db.files.update(id, { failed_reason: undefined, operation_id: newId() });
}

/** Remove: deletes the file (and its pick) from the phone. */
export async function removeFile(id: string): Promise<void> {
  await db.transaction("rw", db.files, db.fileBytes, db.picks, async () => {
    await db.files.delete(id);
    await db.fileBytes.delete(id);
    await db.picks.delete(id);
  });
}

/**
 * Decision 84 #1 round 2 (F3): the picks never kept that the start-up notice names: every one not yet acknowledged with
 * its OK, in the order picked. Reads names and records only, never a file's bytes.
 */
export async function neverKeptToTell(): Promise<Pick[]> {
  const picks = await db.picks.filter((p) => !p.notice_ok).toArray();
  if (!picks.length) return [];
  const files = await db.files.bulkGet(picks.map((p) => p.id));
  return picks.filter((p, i) => neverKept(p, files[i]))
    .sort((a, b) => (a.picked_at === b.picked_at ? (a.order ?? 0) - (b.order ?? 0) : a.picked_at.localeCompare(b.picked_at)));
}

/** The person pressed OK on the start-up notice: those picks are not named in it again (they stay in the Inbox). */
export async function acknowledgeNeverKept(ids: string[]): Promise<void> {
  await db.transaction("rw", db.picks, async () => {
    for (const id of ids) await db.picks.update(id, { notice_ok: true });
  });
}
